import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'
import {
  DollarSign, TrendingUp, TrendingDown, Download, FileText,
  Building2, AlertTriangle, CheckCircle, Clock,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { cn } from '../lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

type RentRollRow = {
  id: string; unit: string; tenant: string; leaseStart: string; leaseEnd: string
  monthlyRent: number; paid: number; balance: number
  status: 'Current' | 'Late' | 'Vacant' | 'Notice'; daysLate: number
}
type PnLLine = { category: string; monthly: number; ytd: number; budget: number; variance: number }
type ExpenseRow = {
  id: string; date: string; category: string; property: string
  vendor: string; amount: number; status: 'Paid' | 'Pending' | 'Overdue'
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const RENT_ROLL: RentRollRow[] = [
  { id: 'r1', unit: 'Unit 4B – Maple Grove', tenant: 'Jordan Blake', leaseStart: '2025-01-01', leaseEnd: '2025-12-31', monthlyRent: 2850, paid: 2850, balance: 0, status: 'Current', daysLate: 0 },
  { id: 'r2', unit: 'Unit 12C – Willow Creek', tenant: 'Avery Kim', leaseStart: '2025-03-01', leaseEnd: '2026-02-28', monthlyRent: 3100, paid: 0, balance: 3100, status: 'Late', daysLate: 12 },
  { id: 'r3', unit: 'Unit 8 – Cedar Ridge', tenant: 'Sam Porter', leaseStart: '2025-02-01', leaseEnd: '2026-01-31', monthlyRent: 2400, paid: 2400, balance: 0, status: 'Current', daysLate: 0 },
  { id: 'r4', unit: 'Unit 2A – Maple Grove', tenant: '—', leaseStart: '—', leaseEnd: '—', monthlyRent: 2200, paid: 0, balance: 0, status: 'Vacant', daysLate: 0 },
]

const PNL_INCOME: PnLLine[] = [
  { category: 'Base Rent', monthly: 8350, ytd: 50100, budget: 8350, variance: 0 },
  { category: 'Late Fees', monthly: 155, ytd: 620, budget: 100, variance: 55 },
  { category: 'Application Fees', monthly: 0, ytd: 150, budget: 50, variance: -50 },
  { category: 'Pet Fees', monthly: 75, ytd: 300, budget: 50, variance: 25 },
]

const PNL_EXPENSES: PnLLine[] = [
  { category: 'Mortgage / Debt Service', monthly: 4200, ytd: 25200, budget: 4200, variance: 0 },
  { category: 'Property Insurance', monthly: 450, ytd: 2700, budget: 450, variance: 0 },
  { category: 'Property Tax', monthly: 380, ytd: 2280, budget: 380, variance: 0 },
  { category: 'Maintenance & Repairs', monthly: 1025, ytd: 4150, budget: 600, variance: 425 },
  { category: 'Property Management Fee', monthly: 835, ytd: 5010, budget: 835, variance: 0 },
  { category: 'Utilities', monthly: 280, ytd: 1680, budget: 250, variance: 30 },
  { category: 'Landscaping', monthly: 120, ytd: 720, budget: 120, variance: 0 },
  { category: 'Advertising / Leasing', monthly: 0, ytd: 450, budget: 150, variance: -150 },
]

const EXPENSES: ExpenseRow[] = [
  { id: 'e1', date: '2026-07-01', category: 'Maintenance', property: 'Maple Grove', vendor: 'Northstar HVAC', amount: 650, status: 'Paid' },
  { id: 'e2', date: '2026-07-02', category: 'Maintenance', property: 'Willow Creek', vendor: 'Rapid Plumbing', amount: 420, status: 'Paid' },
  { id: 'e3', date: '2026-07-03', category: 'Insurance', property: 'All Properties', vendor: 'StateFarm', amount: 450, status: 'Paid' },
  { id: 'e4', date: '2026-07-05', category: 'Landscaping', property: 'Cedar Ridge', vendor: 'GreenCut Co.', amount: 120, status: 'Paid' },
  { id: 'e5', date: '2026-07-07', category: 'Maintenance', property: 'Cedar Ridge', vendor: 'LiftOps Inc.', amount: 1400, status: 'Pending' },
  { id: 'e6', date: '2026-07-10', category: 'Property Tax', property: 'All Properties', vendor: 'County Assessor', amount: 380, status: 'Pending' },
  { id: 'e7', date: '2026-06-28', category: 'Utilities', property: 'Maple Grove', vendor: 'City Power & Gas', amount: 280, status: 'Paid' },
]

const MONTHLY_TREND = [
  { month: 'Jan', income: 8350, expenses: 7100, net: 1250 },
  { month: 'Feb', income: 8505, expenses: 7200, net: 1305 },
  { month: 'Mar', income: 8505, expenses: 7450, net: 1055 },
  { month: 'Apr', income: 8350, expenses: 6950, net: 1400 },
  { month: 'May', income: 8505, expenses: 7100, net: 1405 },
  { month: 'Jun', income: 8580, expenses: 7290, net: 1290 },
  { month: 'Jul', income: 8580, expenses: 7290, net: 1290 },
]

const EXPENSE_PIE = [
  { name: 'Mortgage', value: 4200, color: '#6366f1' },
  { name: 'Maintenance', value: 1025, color: '#f59e0b' },
  { name: 'Mgmt Fee', value: 835, color: '#10b981' },
  { name: 'Insurance', value: 450, color: '#3b82f6' },
  { name: 'Tax', value: 380, color: '#8b5cf6' },
  { name: 'Utilities', value: 280, color: '#06b6d4' },
  { name: 'Other', value: 120, color: '#64748b' },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
const fmtSign = (n: number) => `${n >= 0 ? '+' : ''}${fmt(n)}`

function downloadFile(filename: string, content: string, mimeType = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mimeType })
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(href)
}

function toCsv(headers: string[], rows: Array<Array<string | number>>) {
  const escape = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`
  return [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n')
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, delta, tone, icon: Icon }: {
  label: string; value: string; delta?: string; tone?: 'up' | 'down' | 'neutral'; icon?: React.ElementType
}) {
  return (
    <Card className="p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</p>
          <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-slate-50 leading-none">{value}</p>
          {delta && (
            <p className={cn('mt-1.5 text-xs font-medium',
              tone === 'up' ? 'text-emerald-600 dark:text-emerald-400' :
              tone === 'down' ? 'text-rose-600 dark:text-rose-400' :
              'text-slate-500 dark:text-slate-400'
            )}>{delta}</p>
          )}
        </div>
        {Icon && (
          <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ml-3',
            tone === 'up' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400' :
            tone === 'down' ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/30 dark:text-rose-400' :
            'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
          )}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Card>
  )
}

// ─── P&L Tab ─────────────────────────────────────────────────────────────────

function PnLTab({ onExport }: { onExport: () => void }) {
  const totalIncome = PNL_INCOME.reduce((s, r) => s + r.monthly, 0)
  const totalExpenses = PNL_EXPENSES.reduce((s, r) => s + r.monthly, 0)
  const netIncome = totalIncome - totalExpenses
  const totalYtdIncome = PNL_INCOME.reduce((s, r) => s + r.ytd, 0)
  const totalYtdExpenses = PNL_EXPENSES.reduce((s, r) => s + r.ytd, 0)
  const netYtd = totalYtdIncome - totalYtdExpenses

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="Total Income" value={fmt(totalIncome)} delta="+2.1% vs last month" tone="up" icon={TrendingUp} />
        <StatCard label="Total Expenses" value={fmt(totalExpenses)} delta="+4.8% vs last month" tone="down" icon={TrendingDown} />
        <StatCard label="Net Income" value={fmt(netIncome)} delta="This month" tone={netIncome >= 0 ? 'up' : 'down'} icon={DollarSign} />
        <StatCard label="YTD Net" value={fmt(netYtd)} delta="Jan–Jul 2026" tone="up" icon={TrendingUp} />
      </div>

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="text-base">Monthly Trend</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={MONTHLY_TREND} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => [fmt(v), '']} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="income" name="Income" fill="#10b981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#6366f1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="net" name="Net" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Profit & Loss Statement — July 2026</CardTitle>
          <Button size="sm" variant="secondary" onClick={onExport}><Download className="mr-1.5 h-3.5 w-3.5" />Export PDF</Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[580px] text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/60">
                  {['Category', 'This Month', 'YTD', 'Budget', 'Variance'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-emerald-50/70 dark:bg-emerald-950/20">
                  <td colSpan={5} className="px-4 py-1.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">Income</td>
                </tr>
                {PNL_INCOME.map(row => (
                  <tr key={row.category} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200">{row.category}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-emerald-700 dark:text-emerald-400">{fmt(row.monthly)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-400">{fmt(row.ytd)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500 dark:text-slate-500">{fmt(row.budget)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={cn('text-xs font-semibold', row.variance >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{fmtSign(row.variance)}</span>
                    </td>
                  </tr>
                ))}
                <tr className="bg-emerald-100/60 dark:bg-emerald-950/40">
                  <td className="px-4 py-2.5 font-bold text-emerald-800 dark:text-emerald-200 text-sm">Total Income</td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-800 dark:text-emerald-200">{fmt(totalIncome)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-700 dark:text-emerald-300">{fmt(totalYtdIncome)}</td>
                  <td colSpan={2} />
                </tr>
                <tr className="bg-rose-50/70 dark:bg-rose-950/20">
                  <td colSpan={5} className="px-4 py-1.5 text-[11px] font-bold text-rose-700 dark:text-rose-300 uppercase tracking-wider">Expenses</td>
                </tr>
                {PNL_EXPENSES.map(row => (
                  <tr key={row.category} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200">{row.category}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-rose-700 dark:text-rose-400">({fmt(row.monthly)})</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-400">({fmt(row.ytd)})</td>
                    <td className="px-4 py-2.5 text-right text-slate-500 dark:text-slate-500">({fmt(row.budget)})</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={cn('text-xs font-semibold', row.variance <= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                        {row.variance === 0 ? '—' : fmtSign(-row.variance)}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="bg-rose-100/60 dark:bg-rose-950/40">
                  <td className="px-4 py-2.5 font-bold text-rose-800 dark:text-rose-200 text-sm">Total Expenses</td>
                  <td className="px-4 py-2.5 text-right font-bold text-rose-800 dark:text-rose-200">({fmt(totalExpenses)})</td>
                  <td className="px-4 py-2.5 text-right font-bold text-rose-700 dark:text-rose-300">({fmt(totalYtdExpenses)})</td>
                  <td colSpan={2} />
                </tr>
                <tr className="border-t-2 border-violet-200 dark:border-violet-700 bg-violet-50/60 dark:bg-violet-950/30">
                  <td className="px-4 py-3 font-bold text-violet-900 dark:text-violet-200 text-base">Net Income</td>
                  <td className="px-4 py-3 text-right font-bold text-violet-900 dark:text-violet-200 text-base">{fmt(netIncome)}</td>
                  <td className="px-4 py-3 text-right font-bold text-violet-700 dark:text-violet-300">{fmt(netYtd)}</td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Rent Roll Tab ────────────────────────────────────────────────────────────

function RentRollTab({ onExport }: { onExport: () => void }) {
  const totalUnits = RENT_ROLL.length
  const occupied = RENT_ROLL.filter(r => r.status !== 'Vacant').length
  const totalRent = RENT_ROLL.reduce((s, r) => s + r.monthlyRent, 0)
  const totalPaid = RENT_ROLL.reduce((s, r) => s + r.paid, 0)
  const totalBalance = RENT_ROLL.reduce((s, r) => s + r.balance, 0)
  const occupancyRate = Math.round((occupied / totalUnits) * 100)

  const statusStyle: Record<string, string> = {
    Current: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
    Late: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800',
    Vacant: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600',
    Notice: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
        <StatCard label="Total Units" value={String(totalUnits)} icon={Building2} tone="neutral" />
        <StatCard label="Occupied" value={String(occupied)} delta={`${occupancyRate}% occupancy`} tone="up" icon={CheckCircle} />
        <StatCard label="Monthly Rent" value={fmt(totalRent)} icon={DollarSign} tone="neutral" />
        <StatCard label="Collected" value={fmt(totalPaid)} delta="This month" tone="up" icon={TrendingUp} />
        <StatCard label="Outstanding" value={fmt(totalBalance)} delta={totalBalance > 0 ? 'Needs follow-up' : 'Fully collected'} tone={totalBalance > 0 ? 'down' : 'up'} icon={totalBalance > 0 ? AlertTriangle : CheckCircle} />
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Rent Roll — July 2026</CardTitle>
          <Button size="sm" variant="secondary" onClick={onExport}><Download className="mr-1.5 h-3.5 w-3.5" />Export CSV</Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/60">
                  {['Unit / Property', 'Tenant', 'Lease End', 'Monthly Rent', 'Paid', 'Balance', 'Status'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RENT_ROLL.map(row => (
                  <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.unit}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{row.tenant}</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-500 text-xs">{row.leaseEnd}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.monthlyRent > 0 ? fmt(row.monthlyRent) : '—'}</td>
                    <td className="px-4 py-3 font-semibold text-emerald-700 dark:text-emerald-400">{row.paid > 0 ? fmt(row.paid) : '—'}</td>
                    <td className="px-4 py-3">
                      {row.balance > 0
                        ? <span className="font-bold text-rose-700 dark:text-rose-400">{fmt(row.balance)}{row.daysLate > 0 && <span className="ml-1 text-[10px] font-normal">({row.daysLate}d late)</span>}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold', statusStyle[row.status] ?? statusStyle.Current)}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Expenses Tab ─────────────────────────────────────────────────────────────

function ExpensesTab() {
  const totalAmt = EXPENSES.reduce((s, e) => s + e.amount, 0)
  const pendingAmt = EXPENSES.filter(e => e.status === 'Pending').reduce((s, e) => s + e.amount, 0)
  const statusStyle: Record<string, string> = {
    Paid: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300',
    Pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300',
    Overdue: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300',
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="Total This Month" value={fmt(totalAmt)} tone="down" icon={TrendingDown} delta="Jul 2026" />
        <StatCard label="Pending Payments" value={fmt(pendingAmt)} delta={`${EXPENSES.filter(e => e.status === 'Pending').length} items outstanding`} icon={Clock} tone="neutral" />
        <StatCard label="Largest Category" value="Mortgage" delta={fmt(4200) + ' / mo'} icon={Building2} tone="neutral" />
        <StatCard label="Maintenance Over Budget" value="+71%" delta={fmt(425) + ' overage'} icon={AlertTriangle} tone="down" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="shadow-sm">
          <CardHeader><CardTitle className="text-base">Expense Breakdown</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={EXPENSE_PIE} dataKey="value" nameKey="name" innerRadius={58} outerRadius={88} paddingAngle={2} cornerRadius={5}>
                  {EXPENSE_PIE.map((e) => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v: number) => [fmt(v), '']} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader><CardTitle className="text-base">Expense Ledger</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {EXPENSES.map(e => (
                <div key={e.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{e.vendor}</p>
                    <p className="text-xs text-slate-500">{e.category} · {e.property} · {e.date}</p>
                  </div>
                  <div className="flex items-center gap-3 ml-3 shrink-0">
                    <span className="font-bold text-slate-900 dark:text-slate-100">{fmt(e.amount)}</span>
                    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold', statusStyle[e.status] ?? statusStyle.Pending)}>{e.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// ─── Owner Statement Tab ──────────────────────────────────────────────────────

function OwnerStatementTab({ onDownload, onPrint }: { onDownload: () => void; onPrint: () => void }) {
  const totalIncome = PNL_INCOME.reduce((s, r) => s + r.monthly, 0)
  const totalExpenses = PNL_EXPENSES.reduce((s, r) => s + r.monthly, 0)
  const netIncome = totalIncome - totalExpenses

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Owner Statement — July 2026</h3>
          <p className="text-xs text-slate-500">Portfolio: 3 Properties · XL Property & Contracting</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={onDownload}><Download className="mr-1.5 h-3.5 w-3.5" />Download PDF</Button>
          <Button size="sm" variant="secondary" onClick={onPrint}><FileText className="mr-1.5 h-3.5 w-3.5" />Print</Button>
        </div>
      </div>

      <Card className="border-violet-200 dark:border-violet-800 shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 dark:border-slate-700 pb-5 mb-5">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">XL Property & Contracting</h2>
              <p className="text-sm text-slate-500 mt-1">3 Properties · Portfolio Owner Statement</p>
              <p className="text-sm text-slate-500">Period: July 1–31, 2026</p>
            </div>
            <div className="rounded-2xl bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800 p-4 text-right">
              <p className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide">Net to Owner</p>
              <p className={cn('text-3xl font-bold mt-1', netIncome >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700')}>{fmt(netIncome)}</p>
              <p className="text-xs text-slate-500 mt-1">After all expenses</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-3">INCOME</h4>
              <div className="space-y-0">
                {PNL_INCOME.map(r => (
                  <div key={r.category} className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-sm text-slate-700 dark:text-slate-300">{r.category}</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{fmt(r.monthly)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2.5 mt-1 font-bold">
                  <span className="text-sm text-emerald-700 dark:text-emerald-400">Total Income</span>
                  <span className="text-sm text-emerald-700 dark:text-emerald-400">{fmt(totalIncome)}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 mb-3">EXPENSES</h4>
              <div className="space-y-0">
                {PNL_EXPENSES.map(r => (
                  <div key={r.category} className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-sm text-slate-700 dark:text-slate-300">{r.category}</span>
                    <span className="text-sm font-semibold text-rose-700 dark:text-rose-400">({fmt(r.monthly)})</span>
                  </div>
                ))}
                <div className="flex justify-between py-2.5 mt-1 font-bold">
                  <span className="text-sm text-rose-700 dark:text-rose-400">Total Expenses</span>
                  <span className="text-sm text-rose-700 dark:text-rose-400">({fmt(totalExpenses)})</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 p-4 flex items-center justify-between">
            <span className="font-bold text-violet-900 dark:text-violet-200">Net Income to Owner</span>
            <span className={cn('font-bold text-xl', netIncome >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700')}>{fmt(netIncome)}</span>
          </div>
          <p className="mt-3 text-[11px] text-slate-400 italic">This statement is generated for informational purposes. Consult your CPA for tax preparation.</p>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

type AccountingTab = 'pnl' | 'rentroll' | 'expenses' | 'owner'

export default function AccountingModule() {
  const [activeTab, setActiveTab] = useState<AccountingTab>('pnl')

  const handleExportPnl = () => {
    const lines = [
      '# Profit and Loss Statement - July 2026',
      '',
      '## Income',
      ...PNL_INCOME.map((row) => `- ${row.category}: ${fmt(row.monthly)} (YTD ${fmt(row.ytd)})`),
      '',
      '## Expenses',
      ...PNL_EXPENSES.map((row) => `- ${row.category}: ${fmt(row.monthly)} (YTD ${fmt(row.ytd)})`),
    ]
    downloadFile('pnl-july-2026.md', lines.join('\n'), 'text/markdown;charset=utf-8')
  }

  const handleExportRentRoll = () => {
    const headers = ['Unit', 'Tenant', 'Lease End', 'Monthly Rent', 'Paid', 'Balance', 'Status']
    const rows = RENT_ROLL.map((row) => [
      row.unit,
      row.tenant,
      row.leaseEnd,
      row.monthlyRent,
      row.paid,
      row.balance,
      row.status,
    ])
    downloadFile('rent-roll-july-2026.csv', toCsv(headers, rows), 'text/csv;charset=utf-8')
  }

  const handleOwnerStatementDownload = () => {
    const totalIncome = PNL_INCOME.reduce((s, r) => s + r.monthly, 0)
    const totalExpenses = PNL_EXPENSES.reduce((s, r) => s + r.monthly, 0)
    const content = [
      '# Owner Statement - July 2026',
      '',
      `- Total Income: ${fmt(totalIncome)}`,
      `- Total Expenses: ${fmt(totalExpenses)}`,
      `- Net Income: ${fmt(totalIncome - totalExpenses)}`,
      '',
      '_Generated by RealAssist Accounting Module_',
    ].join('\n')
    downloadFile('owner-statement-july-2026.md', content, 'text/markdown;charset=utf-8')
  }

  const tabs: { id: AccountingTab; label: string; icon: React.ElementType }[] = [
    { id: 'pnl', label: 'P&L', icon: TrendingUp },
    { id: 'rentroll', label: 'Rent Roll', icon: FileText },
    { id: 'expenses', label: 'Expenses', icon: DollarSign },
    { id: 'owner', label: 'Owner Statement', icon: Download },
  ]

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Accounting & Finance</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">Portfolio P&L, rent roll, expenses, and owner reporting</p>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        {tabs.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-all',
                activeTab === t.id
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-white/60 dark:hover:bg-slate-700'
              )}>
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          )
        })}
      </div>

      <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
        {activeTab === 'pnl' && <PnLTab onExport={handleExportPnl} />}
        {activeTab === 'rentroll' && <RentRollTab onExport={handleExportRentRoll} />}
        {activeTab === 'expenses' && <ExpensesTab />}
        {activeTab === 'owner' && (
          <OwnerStatementTab
            onDownload={handleOwnerStatementDownload}
            onPrint={() => window.print()}
          />
        )}
      </motion.div>
    </div>
  )
}
